// ui/src/pages/calendar/ParentCalendar.tsx
//
// The parent per-child calendar (Task 22) — the last of the three role
// views. Scoped to ONE child via `useActiveStudent()`: the URL
// (/parent/:studentId/calendar) is the source of truth, never a selected-
// child store (see useActiveStudent's own doc comment). `GET /calendar`
// requires `studentId` for a PARENT caller and 400s without it —
// `requireStudent(..., 'student:read-own')` resolves co-parents, so this is
// bounded by the CHILD, not the caller.
//
// Past dates show attendance status (HERE/GONE/PLANNED_ABSENT), carried on
// each class entry's `attendanceStatus` field — the calendar handler already
// derives this per (student, class, date) server-side (see
// derivePerClassDayStatus), so this page does no attendance computation of
// its own.
//
// A closure is normal, planned operations, not an alarm — closed days use
// the same neutral slate styling CalendarDayCell already uses (no red), and
// the closed-day panel offers no "Mark absent" action at all: the server
// already refuses to file an absence on a closed day, but the UI must not
// even dangle the option (addendum point 6).
//
// The one interactive affordance is the day-detail panel opened by tapping a
// date: a FUTURE date with a scheduled (non-suppressed) class not already
// covered by a filed absence offers "Mark absent" for each such class,
// reusing the existing POST /attendance/planned-absence flow
// (ParentChildAbsence.tsx). A date that already has a PLANNED_ABSENT class
// shows that absence's details instead, with a "Cancel absence" action
// (DELETE /attendance/planned-absence/:absenceId, Task 26) — only offered
// while the date is still in the future, mirroring the server's own bound
// (addendum point 6); a past absence's card renders with cancel disabled.
// `entry.absenceId` is the value passed to the mutation — the calendar's
// per-class attendance derivation must carry it through, since the absence's
// primary key is (studentId, date, classId), not this id.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useToast } from '../../components/Toast';
import CalendarGrid from '../../components/calendar/CalendarGrid';
import type { CalendarDay, CalendarMode } from '../../components/calendar/types';
import {
  useGetCalendarQuery,
  useListClosuresQuery,
  useReportPlannedAbsenceMutation,
  useCancelPlannedAbsenceMutation,
} from '../../store/api/localApi';
import { ClosureStatus, DayClassStatus } from '../../types';
import { planAbsenceRange, formatRangeLabel, type RangeDay, classesInPlan } from './absence-range';

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Monday-first index (0-6, Mon=0), matching CalendarGrid's own convention. */
function mondayFirstIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** First/last day of the month containing `date`. */
function monthRange(date: Date): { from: string; to: string } {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: toIsoDate(first), to: toIsoDate(last) };
}

/** Monday-to-Sunday week containing `date`. */
function weekRange(date: Date): { from: string; to: string } {
  const start = new Date(date);
  start.setDate(start.getDate() - mondayFirstIndex(date));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

const ParentCalendar: React.FC = () => {
  const { activeStudent, isLoading: studentLoading } = useActiveStudent();
  const { showSuccess, showError } = useToast();

  // A LEFT child is read-only (Task 17): parents keep access to history but
  // cannot file new absences. So the "Report an absence" affordance — and the
  // ?report=1 auto-enter — are only offered for a non-LEFT child.
  const canReportAbsence = activeStudent?.status !== 'LEFT';

  const [mode, setMode] = useState<CalendarMode>('month');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [filingClassId, setFilingClassId] = useState<string | undefined>(undefined);

  const todayIso = toIsoDate(new Date());
  const range = mode === 'month' ? monthRange(anchorDate) : weekRange(anchorDate);

  const studentId = activeStudent?.id;

  const {
    data: calendarData,
    isLoading: calendarLoading,
    isFetching: calendarFetching,
  } = useGetCalendarQuery({ from: range.from, to: range.to, studentId }, { skip: !studentId });

  // Location-level closures within the visible range, fetched directly —
  // not inferred from a suppressed class entry. `resolveSchedule` emits no
  // row at all for a weekday none of the child's classes run, so a Saturday
  // inside a closure range has no class entries and would otherwise render
  // as an ordinary open day. AdminCalendar and StaffCalendar hit the same
  // trap and fix it the same way (addendum point 3).
  const { data: closuresData } = useListClosuresQuery(
    { locationId: activeStudent?.locationId ?? '', from: range.from, to: range.to },
    { skip: !activeStudent?.locationId }
  );

  const closureByDate = useMemo(() => {
    const map = new Map<string, string>();
    for (const closure of closuresData?.closures ?? []) {
      if (closure.status !== ClosureStatus.CLOSED) continue;
      let cursor = new Date(closure.startDate);
      const end = new Date(closure.endDate);
      while (cursor <= end) {
        map.set(toIsoDate(cursor), closure.label);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [closuresData]);

  const days: CalendarDay[] = useMemo(
    () =>
      (calendarData?.days ?? []).map((day) => ({
        date: day.date,
        classes: day.classes.map((entry) => ({
          classId: entry.classId,
          className: entry.className,
          startTime: entry.startTime,
          endTime: entry.endTime,
          suppressedBy: entry.suppressedBy,
          closureLabel: entry.closureLabel,
          overrideReason: entry.overrideReason,
        })),
        closureLabel: closureByDate.get(day.date),
      })),
    [calendarData, closureByDate]
  );

  const rawDayByDate = useMemo(
    () => new Map((calendarData?.days ?? []).map((d) => [d.date, d])),
    [calendarData]
  );

  const selectedDay = selectedDate ? rawDayByDate.get(selectedDate) : undefined;
  const selectedIsClosed = Boolean(selectedDate && closureByDate.get(selectedDate));
  // Filing: today counts. The backend accepts "today or later", and a parent
  // whose child woke up sick is the commonest reason to open this page —
  // excluding today here (and in range mode below) is what made "mark absent
  // today" silently do nothing. Cancelling stays future-only: the backend
  // refuses to cancel an absence on or before today.
  const selectedIsTodayOrFuture = Boolean(selectedDate && selectedDate >= todayIso);
  const selectedIsFuture = Boolean(selectedDate && selectedDate > todayIso);

  const [reportAbsence, { isLoading: isFiling }] = useReportPlannedAbsenceMutation();
  const [cancelAbsence, { isLoading: isCancelling }] = useCancelPlannedAbsenceMutation();
  const [cancellingId, setCancellingId] = useState<string | undefined>(undefined);

  // ── Absence-report mode ──────────────────────────────────────────────
  // An explicit mode so it's obvious the calendar is where you mark a child
  // off (rather than a hidden day-click). Tap a start day then an end day to
  // choose a range; a single tap is a one-day absence.
  const [absenceMode, setAbsenceMode] = useState(false);
  const [rangeStart, setRangeStart] = useState<string | undefined>(undefined);
  const [rangeEnd, setRangeEnd] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [filingRange, setFilingRange] = useState(false);
  // Classes the parent has unticked for this range ("no after school this
  // week, still coming to Breakfast Club"). Cleared with the range.
  const [excludedClassIds, setExcludedClassIds] = useState<ReadonlySet<string>>(new Set());

  const enterAbsenceMode = () => {
    setAbsenceMode(true);
    setSelectedDate(undefined);
    setRangeStart(undefined);
    setRangeEnd(undefined);
    setReason('');
    setExcludedClassIds(new Set());
  };

  // Arriving via the "Report absence" nav / the retired absence-form redirect
  // (…/calendar?report=1) drops the parent straight into absence mode, then the
  // param is cleared so a later refresh doesn't force the mode back on.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('report') === '1') {
      if (canReportAbsence) setAbsenceMode(true);
      const next = new URLSearchParams(searchParams);
      next.delete('report');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReportAbsence]);
  const exitAbsenceMode = () => {
    setAbsenceMode(false);
    setRangeStart(undefined);
    setRangeEnd(undefined);
    setReason('');
    setExcludedClassIds(new Set());
  };

  // Everything the range COULD file (skips non-class days, closures, and
  // already-absent classes). The tick-box list comes from this one, so an
  // unticked class stays on offer.
  const fullRangePlan = useMemo(() => {
    if (!rangeStart || !rangeEnd) return null;
    return planAbsenceRange(
      rangeStart,
      rangeEnd,
      todayIso,
      rawDayByDate as unknown as Map<string, RangeDay>,
      DayClassStatus.PLANNED_ABSENT
    );
  }, [rangeStart, rangeEnd, todayIso, rawDayByDate]);
  const rangeClasses = useMemo(
    () => (fullRangePlan ? classesInPlan(fullRangePlan) : []),
    [fullRangePlan]
  );

  // What a confirm will actually file: the full plan minus unticked classes.
  const rangePlan = useMemo(() => {
    if (!rangeStart || !rangeEnd || !fullRangePlan) return null;
    if (excludedClassIds.size === 0) return fullRangePlan;
    const keep = new Set(
      rangeClasses.map((c) => c.classId).filter((id) => !excludedClassIds.has(id))
    );
    return planAbsenceRange(
      rangeStart,
      rangeEnd,
      todayIso,
      rawDayByDate as unknown as Map<string, RangeDay>,
      DayClassStatus.PLANNED_ABSENT,
      { classIds: keep }
    );
  }, [rangeStart, rangeEnd, todayIso, rawDayByDate, fullRangePlan, rangeClasses, excludedClassIds]);

  const toggleClass = (classId: string) => {
    setExcludedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) next.delete(classId);
      else next.add(classId);
      return next;
    });
  };

  // The days to highlight in absence mode: the whole selected range, or just
  // the start if only one end is chosen.
  const highlightedDates = useMemo(() => {
    if (!absenceMode || !rangeStart) return undefined;
    const set = new Set<string>();
    const end = rangeEnd ?? rangeStart;
    const cursor = new Date(`${rangeStart}T00:00:00Z`);
    const last = new Date(`${end}T00:00:00Z`);
    while (cursor <= last) {
      set.add(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return set as ReadonlySet<string>;
  }, [absenceMode, rangeStart, rangeEnd]);

  async function handleFileRange() {
    if (!studentId || !rangePlan || rangePlan.fileable.length === 0) return;
    setFilingRange(true);
    let ok = 0;
    let failed = 0;
    let firstError: string | undefined;
    for (const item of rangePlan.fileable) {
      try {
        await reportAbsence({
          studentId,
          classId: item.classId,
          date: item.date,
          notes: reason.trim() || undefined,
        }).unwrap();
        ok += 1;
      } catch (err: any) {
        failed += 1;
        // Surface the actual reason (e.g. REGISTRATION_PENDING's message) rather
        // than a vague count — the backend gate messages are parent-facing.
        firstError = firstError || err?.data?.message || err?.data?.error;
      }
    }
    setFilingRange(false);
    if (failed === 0) {
      showSuccess(
        `Absence reported for ${rangePlan.fileableDates.length} ${
          rangePlan.fileableDates.length === 1 ? 'day' : 'days'
        }.`
      );
      exitAbsenceMode();
    } else if (ok === 0) {
      // Nothing filed — show the real reason (the whole range failed the same way).
      showError(firstError || 'Could not report the absence. Please try again.');
    } else {
      showError(
        `${ok} of ${ok + failed} days filed${
          firstError ? ` — ${firstError}` : ''
        }. Please review the calendar.`
      );
    }
  }

  function handleNavigate(direction: 'prev' | 'next' | 'today') {
    setAnchorDate((prev) => {
      if (direction === 'today') return new Date();
      const next = new Date(prev);
      if (mode === 'month') {
        next.setMonth(next.getMonth() + (direction === 'next' ? 1 : -1));
      } else {
        next.setDate(next.getDate() + (direction === 'next' ? 7 : -7));
      }
      return next;
    });
  }

  function handleDayClick(date: string) {
    if (!absenceMode) {
      setSelectedDate(date);
      return;
    }
    // Range selection: first tap sets the start; second tap sets the end.
    // Tapping again (once a range exists) starts a fresh selection. Past taps
    // are ignored — today and later can be marked off.
    if (date < todayIso) return;
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(date);
      setRangeEnd(undefined);
      setExcludedClassIds(new Set());
    } else {
      // second tap — order the two so start <= end
      if (date < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(date);
      } else {
        setRangeEnd(date);
      }
    }
  }

  async function handleMarkAbsent(classId: string) {
    if (!studentId || !selectedDate) return;
    setFilingClassId(classId);
    try {
      await reportAbsence({ studentId, classId, date: selectedDate }).unwrap();
      showSuccess('Absence reported.');
    } catch (error: any) {
      showError(
        error?.data?.error || error?.data?.message || 'Failed to report absence. Please try again.'
      );
    } finally {
      setFilingClassId(undefined);
    }
  }

  async function handleCancelAbsence(absenceId: string) {
    setCancellingId(absenceId);
    try {
      await cancelAbsence({ absenceId }).unwrap();
      showSuccess('Absence cancelled.');
    } catch (error: any) {
      // A double-cancel (already cancelled elsewhere, e.g. another tab) is a
      // clean 404 from the server, not a genuine failure — the absence is
      // already gone from the caller's perspective, so a fresh calendar
      // refetch resolves it rather than surfacing an error toast.
      if (error?.status !== 404 && error?.originalStatus !== 404) {
        showError(
          error?.data?.error ||
            error?.data?.message ||
            'Failed to cancel absence. Please try again.'
        );
      }
    } finally {
      setCancellingId(undefined);
    }
  }

  if (studentLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="py-6" data-testid="parent-calendar-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Calendar
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {activeStudent
                ? `${activeStudent.displayName}'s schedule and closures`
                : 'Schedule and closures'}
            </p>
          </div>
          {!canReportAbsence ? null : !absenceMode ? (
            <button
              type="button"
              data-testid="parent-report-absence"
              onClick={enterAbsenceMode}
              className="inline-flex items-center rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
            >
              Report an absence
            </button>
          ) : (
            <button
              type="button"
              data-testid="parent-absence-cancel-mode"
              onClick={exitAbsenceMode}
              className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>

        {absenceMode && (
          <div
            data-testid="parent-absence-banner"
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4"
            role="status"
          >
            <p className="text-sm font-semibold text-amber-900">
              Reporting an absence for {activeStudent?.displayName ?? 'your child'}
            </p>
            <p className="mt-1 text-sm text-amber-800">
              {!rangeStart
                ? 'Tap the first day your child will be away. For a single day, tap it, then tap it again — or just tap one day and confirm.'
                : !rangeEnd
                ? 'Now tap the last day away (or tap the same day again for a single day).'
                : `${formatRangeLabel(rangeStart, rangeEnd)} selected. Review below and confirm.`}
            </p>
          </div>
        )}

        {calendarLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            <p className="mt-2 text-sm text-gray-500">Loading calendar...</p>
          </div>
        ) : (
          <div
            data-testid="calendar-fetching"
            data-fetching={calendarFetching}
            className={calendarFetching ? 'opacity-60 transition-opacity' : ''}
          >
            <CalendarGrid
              mode={mode}
              days={days}
              onDayClick={handleDayClick}
              onNavigate={handleNavigate}
              onModeChange={setMode}
              highlightedDates={highlightedDates}
              dayAction={() =>
                absenceMode
                  ? { label: 'select absence day', hoverCue: 'Select day' }
                  : { label: 'view day details', hoverCue: 'View day' }
              }
            />
          </div>
        )}

        {/* Absence-mode confirm panel: shows exactly what will be filed once a
            range is chosen, one reason for the whole range, then confirm. */}
        {absenceMode && rangePlan && (
          <div
            data-testid="parent-absence-confirm"
            className="mt-6 rounded-lg border border-amber-200 bg-white p-4 shadow"
          >
            {fullRangePlan && fullRangePlan.fileable.length === 0 ? (
              <p data-testid="parent-absence-none" className="text-sm text-gray-600">
                No school days to mark in {formatRangeLabel(rangeStart!, rangeEnd ?? rangeStart!)} —
                your child has no scheduled classes on those days (or they’re already marked absent
                or the centre is closed).
              </p>
            ) : (
              <>
                {/* Only offered when the range holds more than one class — with
                    one class there is nothing to choose, and the list would
                    just be noise. */}
                {rangeClasses.length > 1 && (
                  <fieldset data-testid="parent-absence-classes" className="mb-3">
                    <legend className="text-sm font-medium text-gray-900">
                      Which classes?{' '}
                      <span className="font-normal text-gray-400">
                        Untick any your child will still attend.
                      </span>
                    </legend>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      {rangeClasses.map((c) => (
                        <label
                          key={c.classId}
                          data-testid={`parent-absence-class-${c.classId}`}
                          className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            data-testid={`parent-absence-class-${c.classId}-input`}
                            checked={!excludedClassIds.has(c.classId)}
                            onChange={() => toggleClass(c.classId)}
                            className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          {c.className}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                {rangePlan.fileable.length === 0 ? (
                  <p
                    data-testid="parent-absence-no-classes-ticked"
                    aria-live="polite"
                    className="text-sm text-gray-600"
                  >
                    Tick at least one class to mark absent.
                  </p>
                ) : (
                <p className="text-sm font-medium text-gray-900">
                  {rangePlan.fileableDates.length}{' '}
                  {rangePlan.fileableDates.length === 1 ? 'school day' : 'school days'} in{' '}
                  {formatRangeLabel(rangeStart!, rangeEnd ?? rangeStart!)} will be marked absent:
                </p>
                )}
                <ul data-testid="parent-absence-dates" className="mt-2 space-y-1">
                  {rangePlan.fileableDates.map((d) => (
                    <li key={d} className="text-sm text-gray-700">
                      {formatRangeLabel(d, d)} —{' '}
                      {rangePlan.fileable
                        .filter((f) => f.date === d)
                        .map((f) => f.className)
                        .join(', ')}
                    </li>
                  ))}
                </ul>
                {/* From the FULL plan: these are the days with nothing to file
                    whatever is ticked. A day emptied by unticking is explained by
                    the tick boxes above, not counted here. */}
                {fullRangePlan && fullRangePlan.skippedNoClassDates.length > 0 && (
                  <p className="mt-2 text-xs text-gray-400">
                    {fullRangePlan.skippedNoClassDates.length} day
                    {fullRangePlan.skippedNoClassDates.length === 1 ? '' : 's'} in the range skipped
                    (no class, weekend, or closure).
                  </p>
                )}

                <label
                  htmlFor="parent-absence-reason"
                  className="mt-4 block text-sm font-medium text-gray-700"
                >
                  Reason <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="parent-absence-reason"
                  data-testid="parent-absence-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. Family holiday"
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    data-testid="parent-absence-confirm-cancel"
                    onClick={() => {
                      setRangeStart(undefined);
                      setRangeEnd(undefined);
                      setExcludedClassIds(new Set());
                    }}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    data-testid="parent-absence-confirm-submit"
                    onClick={handleFileRange}
                    disabled={filingRange || rangePlan.fileable.length === 0}
                    className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    {filingRange
                      ? 'Reporting…'
                      : `Mark ${formatRangeLabel(rangeStart!, rangeEnd ?? rangeStart!)} absent`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {!absenceMode && selectedDate && (
          <div
            data-testid="parent-calendar-day-panel"
            className="mt-6 bg-white shadow rounded-lg overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">{selectedDate}</h3>
              <button
                type="button"
                data-testid="parent-calendar-day-panel-close"
                onClick={() => setSelectedDate(undefined)}
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                Close
              </button>
            </div>

            {selectedIsClosed ? (
              <div className="px-4 py-4 text-sm text-slate-600">
                Closed — {closureByDate.get(selectedDate)}
              </div>
            ) : !selectedDay || selectedDay.classes.length === 0 ? (
              <div className="px-4 py-4 text-sm text-gray-500">No classes scheduled.</div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {selectedDay.classes.map((entry) => {
                  const isPlannedAbsent = entry.attendanceStatus === DayClassStatus.PLANNED_ABSENT;
                  const canFile = selectedIsTodayOrFuture && !entry.suppressedBy && !isPlannedAbsent;
                  return (
                    <li
                      key={entry.classId}
                      data-testid={`parent-calendar-day-class-${entry.classId}`}
                      className="px-4 py-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{entry.className}</p>
                          <p className="text-xs text-gray-500">
                            {entry.startTime} – {entry.endTime}
                          </p>
                        </div>
                        {isPlannedAbsent ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Absent
                          </span>
                        ) : entry.attendanceStatus ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700">
                            {entry.attendanceStatus}
                          </span>
                        ) : canFile ? (
                          <button
                            type="button"
                            data-testid="parent-calendar-mark-absent"
                            onClick={() => handleMarkAbsent(entry.classId)}
                            disabled={isFiling && filingClassId === entry.classId}
                            className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {isFiling && filingClassId === entry.classId
                              ? 'Reporting…'
                              : 'Mark absent'}
                          </button>
                        ) : null}
                      </div>

                      {isPlannedAbsent && (
                        <div
                          data-testid={`parent-calendar-absence-${selectedDate}`}
                          className="mt-2 rounded-md bg-amber-50 px-3 py-2"
                        >
                          <p className="text-xs text-amber-800">
                            {entry.notes ? entry.notes : 'Absence filed — no reason given.'}
                          </p>
                          {selectedIsFuture && entry.absenceId ? (
                            <button
                              type="button"
                              data-testid="parent-calendar-cancel-absence"
                              onClick={() => handleCancelAbsence(entry.absenceId!)}
                              disabled={isCancelling && cancellingId === entry.absenceId}
                              className="mt-2 inline-flex items-center px-2.5 py-1 rounded-md border border-amber-300 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                            >
                              {isCancelling && cancellingId === entry.absenceId
                                ? 'Cancelling…'
                                : 'Cancel absence'}
                            </button>
                          ) : (
                            !selectedIsFuture && (
                              <button
                                type="button"
                                data-testid="parent-calendar-cancel-absence"
                                disabled
                                title="An absence on or before today can't be cancelled"
                                className="mt-2 inline-flex items-center px-2.5 py-1 rounded-md border border-amber-300 text-xs font-medium text-amber-700 opacity-50 cursor-not-allowed"
                              >
                                Cancel absence
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentCalendar;
